import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, X, Trash2, Edit2, AlertCircle, Package, Save, ArrowRight } from "lucide-react";

const STATUS_FLOW = [
  { v: "new", label: "New" },
  { v: "confirmed", label: "Confirmed" },
  { v: "sent_to_etchery", label: "Sent to The Etchery" },
  { v: "in_production", label: "In Production" },
  { v: "ready", label: "Ready For Collection" },
  { v: "completed", label: "Completed" },
];
const ISSUE_STATUS = { v: "issue", label: "Issue / Rework" };

const ALL_STATUSES = [...STATUS_FLOW, ISSUE_STATUS];

const EMPTY_LINE = { plant_name: "", style: "standard", colour: "", quantity: 1, unit_price: 4.5, production_cost: 1.2 };
const EMPTY = { customer_name: "", grower_name: "", email: "", phone: "", lines: [{ ...EMPTY_LINE }], artwork_url: "", notes_url: "", design_url: "", notes: "" };

export default function TagOrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/admin/etchery/tag-orders${statusFilter ? `?status=${statusFilter}` : ""}`);
    setOrders(data.orders || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [statusFilter]);

  const remove = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    await api.delete(`/admin/etchery/tag-orders/${id}`);
    load();
  };

  const setStatus = async (id, status) => {
    await api.post(`/admin/etchery/tag-orders/${id}/status`, { status });
    load();
    if (editing && editing.id === id) {
      const { data } = await api.get(`/admin/etchery/tag-orders/${id}`);
      setEditing(data.order);
    }
  };

  const save = async (form) => {
    const payload = {
      customer_name: form.customer_name.trim(),
      grower_name: form.grower_name?.trim() || "",
      email: form.email?.trim() || "",
      phone: form.phone?.trim() || "",
      lines: form.lines.map((l) => ({
        plant_name: l.plant_name.trim(),
        tag_product_id: l.tag_product_id || "",
        style: l.style || "standard",
        colour: l.colour || "",
        quantity: Number(l.quantity) || 1,
        unit_price: Number(l.unit_price) || 0,
        production_cost: Number(l.production_cost) || 0,
      })).filter((l) => l.plant_name),
      artwork_url: form.artwork_url || "",
      notes_url: form.notes_url || "",
      design_url: form.design_url || "",
      notes: form.notes || "",
    };
    if (!payload.lines.length) throw new Error("Add at least one line item with a plant name.");
    if (form.id) {
      await api.patch(`/admin/etchery/tag-orders/${form.id}`, payload);
    } else {
      await api.post("/admin/etchery/tag-orders", payload);
    }
    setEditing(null);
    load();
  };

  return (
    <div data-testid="etchery-tag-orders">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="font-serif text-2xl">Tag Orders</h2>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bloom-input max-w-xs" data-testid="orders-status-filter">
          <option value="">All statuses</option>
          {ALL_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <button onClick={() => setEditing({ ...EMPTY })} className="bloom-btn bloom-btn-primary ml-auto" data-testid="order-new-btn">
          <Plus size={14} /> New order
        </button>
      </div>

      {loading ? <Empty msg="Loading…" /> : orders.length === 0 ? <Empty msg="No orders yet. Create the first one." /> : (
        <div className="space-y-3">
          {orders.map((o) => (
            <OrderRow key={o.id} order={o} onOpen={() => setEditing(o)} onDelete={() => remove(o.id)} onStatus={setStatus} />
          ))}
        </div>
      )}

      {editing && <OrderEditor initial={editing} onClose={() => setEditing(null)} onSave={save} onStatus={setStatus} />}
    </div>
  );
}

function OrderRow({ order, onOpen, onDelete, onStatus }) {
  const status = ALL_STATUSES.find((s) => s.v === order.status) || ALL_STATUSES[0];
  const nextIdx = STATUS_FLOW.findIndex((s) => s.v === order.status);
  const next = order.status !== "issue" && nextIdx >= 0 && nextIdx < STATUS_FLOW.length - 1 ? STATUS_FLOW[nextIdx + 1] : null;
  return (
    <article className="bloom-card p-4" data-testid={`order-card-${order.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="font-mono text-xs text-[var(--bloom-ink-muted)] mt-1">#{order.order_number || "—"}</div>
        <button onClick={onOpen} className="text-left flex-1 min-w-0 hover:opacity-80">
          <div className="font-serif text-lg leading-tight">{order.customer_name}</div>
          <div className="text-xs text-[var(--bloom-ink-soft)] mt-0.5">
            {order.lines.length} line{order.lines.length !== 1 ? "s" : ""} · {order.totals.quantity} tags · ${order.totals.revenue.toFixed(2)} revenue · ${order.totals.profit.toFixed(2)} profit
          </div>
          {order.grower_name && <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mt-1">Grower · {order.grower_name}</div>}
        </button>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] tracking-[0.2em] uppercase border px-2 py-1 ${order.status === "issue" ? "border-[var(--bloom-accent)] text-[var(--bloom-accent)]" : order.status === "completed" ? "border-[var(--bloom-ink)] text-[var(--bloom-ink)]" : "border-[var(--bloom-border)] text-[var(--bloom-ink-soft)]"}`}>{status.label}</span>
          {next && (
            <button onClick={() => onStatus(order.id, next.v)} className="bloom-btn bloom-btn-outline text-xs" data-testid={`order-advance-${order.id}`} title={`Advance to ${next.label}`}>
              <ArrowRight size={12} /> {next.label}
            </button>
          )}
          <button onClick={onDelete} className="p-1.5 hover:bg-[var(--bloom-surface)] text-[var(--bloom-accent)]" data-testid={`order-delete-${order.id}`}><Trash2 size={13} /></button>
        </div>
      </div>
    </article>
  );
}

function OrderEditor({ initial, onClose, onSave, onStatus }) {
  const [form, setForm] = useState({ ...initial, lines: initial.lines?.length ? initial.lines : [{ ...EMPTY_LINE }] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm((f) => { const ls = [...f.lines]; ls[i] = { ...ls[i], [k]: v }; return { ...f, lines: ls }; });
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await onSave(form); } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); } finally { setBusy(false); }
  };

  const isEditing = !!form.id;
  // Compute totals client-side for the editor preview
  const totals = form.lines.reduce((acc, l) => {
    const qty = Number(l.quantity) || 0;
    const up = Number(l.unit_price) || 0;
    const pc = Number(l.production_cost) || 0;
    acc.qty += qty; acc.rev += qty * up; acc.cost += qty * pc;
    return acc;
  }, { qty: 0, rev: 0, cost: 0 });

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="order-editor-modal">
      <div className="bg-[var(--bloom-bg)] w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bloom-border)]">
          <h2 className="font-serif text-2xl flex items-center gap-2">
            <Package size={18} strokeWidth={1.5} />
            {isEditing ? `Order #${form.order_number}` : "New tag order"}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bloom-surface)]"><X size={16} /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <SectionTitle>Customer</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Customer name *"><input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="bloom-input" autoFocus data-testid="order-customer" /></Field>
            <Field label="Grower name (if applicable)"><input value={form.grower_name || ""} onChange={(e) => set("grower_name", e.target.value)} className="bloom-input" data-testid="order-grower" /></Field>
            <Field label="Email"><input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="bloom-input" /></Field>
            <Field label="Phone"><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bloom-input" /></Field>
          </div>

          <SectionTitle>Line items</SectionTitle>
          <div className="space-y-3" data-testid="order-lines">
            {form.lines.map((l, i) => (
              <div key={i} className="bloom-card p-3 grid grid-cols-12 gap-2 items-end">
                <div className="col-span-12 md:col-span-4"><Field label={`Plant ${i + 1}`}><input value={l.plant_name} onChange={(e) => setLine(i, "plant_name", e.target.value)} className="bloom-input" data-testid={`order-line-plant-${i}`} /></Field></div>
                <div className="col-span-4 md:col-span-2"><Field label="Style"><select value={l.style} onChange={(e) => setLine(i, "style", e.target.value)} className="bloom-input">{STYLES_FOR_LINE.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}</select></Field></div>
                <div className="col-span-4 md:col-span-2"><Field label="Colour"><input value={l.colour || ""} onChange={(e) => setLine(i, "colour", e.target.value)} className="bloom-input" /></Field></div>
                <div className="col-span-4 md:col-span-1"><Field label="Qty"><input type="number" min="1" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="bloom-input" data-testid={`order-line-qty-${i}`} /></Field></div>
                <div className="col-span-6 md:col-span-1"><Field label="Unit $"><input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className="bloom-input" data-testid={`order-line-price-${i}`} /></Field></div>
                <div className="col-span-6 md:col-span-1"><Field label="Cost $"><input type="number" step="0.01" value={l.production_cost} onChange={(e) => setLine(i, "production_cost", e.target.value)} className="bloom-input" data-testid={`order-line-cost-${i}`} /></Field></div>
                <div className="col-span-12 md:col-span-1 flex justify-end">{form.lines.length > 1 && (<button type="button" onClick={() => removeLine(i)} className="p-2 text-[var(--bloom-accent)] hover:bg-[var(--bloom-surface)]" aria-label="Remove line"><Trash2 size={13} /></button>)}</div>
              </div>
            ))}
          </div>
          <button type="button" onClick={addLine} className="bloom-btn bloom-btn-outline text-sm" data-testid="order-add-line"><Plus size={12} /> Add another plant</button>

          {/* Totals */}
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[var(--bloom-border)]">
            <Tile label="Tags" v={totals.qty} />
            <Tile label="Revenue" v={`$${totals.rev.toFixed(2)}`} />
            <Tile label="Profit" v={`$${(totals.rev - totals.cost).toFixed(2)}`} />
          </div>

          <SectionTitle>Files &amp; notes</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Artwork URL"><input value={form.artwork_url || ""} onChange={(e) => set("artwork_url", e.target.value)} className="bloom-input" placeholder="https://…" /></Field>
            <Field label="Design URL"><input value={form.design_url || ""} onChange={(e) => set("design_url", e.target.value)} className="bloom-input" placeholder="https://…" /></Field>
            <Field label="Notes URL"><input value={form.notes_url || ""} onChange={(e) => set("notes_url", e.target.value)} className="bloom-input" placeholder="https://…" /></Field>
          </div>
          <Field label="Internal notes"><textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="bloom-input" /></Field>

          {/* Status section for existing orders */}
          {isEditing && (
            <>
              <SectionTitle>Status &amp; timeline</SectionTitle>
              <div className="flex flex-wrap gap-2">
                {ALL_STATUSES.map((s) => (
                  <button key={s.v} type="button" onClick={() => onStatus(form.id, s.v)} disabled={form.status === s.v} className={`px-3 py-1.5 text-xs tracking-[0.15em] uppercase border ${form.status === s.v ? "border-[var(--bloom-ink)] bg-[var(--bloom-surface)]" : "border-[var(--bloom-border)] hover:border-[var(--bloom-ink-soft)]"} ${s.v === "issue" ? "text-[var(--bloom-accent)]" : ""}`} data-testid={`order-set-status-${s.v}`}>{s.label}</button>
                ))}
              </div>
              {form.timeline?.length > 0 && (
                <ol className="space-y-2 text-xs font-serif">
                  {[...form.timeline].reverse().map((t, i) => (
                    <li key={`${t.at}-${i}`} className="border-l-2 border-[var(--bloom-border)] pl-3" data-testid={`timeline-entry-${i}`}>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">{new Date(t.at).toLocaleString()}</div>
                      <div>{t.from ? `${t.from} → ` : ""}<strong>{t.to}</strong> · {t.by_name}{t.note ? ` — ${t.note}` : ""}</div>
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}

          {err && <div className="flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic"><AlertCircle size={14} /> {err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="bloom-btn bloom-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="order-save"><Save size={12} /> {busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const STYLES_FOR_LINE = [
  { v: "standard", label: "Standard" },
  { v: "premium", label: "Premium" },
];

function SectionTitle({ children }) { return <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] pt-2">{children}</div>; }
function Field({ label, children }) { return <div><div className="bloom-label mb-1.5">{label}</div>{children}</div>; }
function Tile({ label, v }) { return <div className="bloom-card p-3 text-center"><div className="bloom-label mb-1">{label}</div><div className="font-serif text-xl">{v}</div></div>; }
function Empty({ msg }) { return <div className="bloom-card p-14 text-center font-serif italic text-[var(--bloom-ink-muted)]">{msg}</div>; }
